import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries } from '@/lib/queryHelpers'
import type { ApiResponse } from '@/types'
import type { ParkingFeeConfig, VehicleType } from '@/types/parking'

// Query keys
export const parkingFeeConfigKeys = {
  all: ['parking_fee_configs'] as const,
  lists: () => [...parkingFeeConfigKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...parkingFeeConfigKeys.lists(), filters] as const,
}

// Fetch fee configs for a property
export function useFeeConfigs(propertyId: number) {
  return useQuery({
    queryKey: parkingFeeConfigKeys.list({ propertyId }),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<ParkingFeeConfig[]>>(
        `/api/properties/${propertyId}/parking_fee_configs`
      )
      return data
    },
    enabled: !!propertyId,
  })
}

// Upsert fee configs (PUT collection endpoint)
export interface UpsertFeeConfigsInput {
  property_id: number
  configs: Array<{
    vehicle_type: VehicleType
    included_spots: number
    addon_monthly_fee: number
    external_monthly_fee: number
    external_daily_fee: number
  }>
}

export function useUpsertFeeConfigs() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ property_id, configs }: UpsertFeeConfigsInput) => {
      const { data } = await api.put<ApiResponse<ParkingFeeConfig[]>>(
        `/api/properties/${property_id}/parking_fee_configs`,
        { parking_fee_configs: configs }
      )
      return data.data
    },
    onSuccess: (_data, variables) => {
      removeListQueries(queryClient, parkingFeeConfigKeys.list({ propertyId: variables.property_id }))
    },
  })
}
